import {objectFromEntries} from '@benchling/js-utils';

import {LookupConfigModalPropsWithoutExtraState} from 'lookup-config/LookupConfigModal';
import {
  ContainerByGridPositionLookupStepSpec,
  CountLookupStepSpec,
  LookupStepSpec,
  LookupStepType,
  ProcessTaskLookupStepSpec,
  SchemaFieldLookupStepSpec,
} from 'lookup-config/LookupConfigModalConstants';
import {FIELD_TYPES, LINK_FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';

// Keep in sync with SnapshotFieldLookupStepListSpecEditor.SUPPORTED_STARTING_LOOKUP_STEPS
const FIRST_LOOKUP_STEP_TYPES = [
  LookupStepType.SCHEMA_FIELD,
  LookupStepType.COUNT,
  LookupStepType.CONTAINER_BY_GRID_POSITION,
  LookupStepType.PROCESS_TASK,
];

export function isValidFirstSnapshotFieldStep(
  lookupStep: LookupStepSpec
): lookupStep is
  | SchemaFieldLookupStepSpec
  | CountLookupStepSpec
  | ContainerByGridPositionLookupStepSpec
  | ProcessTaskLookupStepSpec {
  return FIRST_LOOKUP_STEP_TYPES.includes(lookupStep.type);
}

export function isFirstLookupStepWithSchemaField(
  lookupStep:
    | SchemaFieldLookupStepSpec
    | CountLookupStepSpec
    | ContainerByGridPositionLookupStepSpec
    | ProcessTaskLookupStepSpec
): lookupStep is SchemaFieldLookupStepSpec | CountLookupStepSpec | ContainerByGridPositionLookupStepSpec {
  return [
    LookupStepType.SCHEMA_FIELD,
    LookupStepType.COUNT,
    LookupStepType.CONTAINER_BY_GRID_POSITION,
  ].includes(lookupStep.type);
}

export type SnapshotFieldLookupConfigModalProps = Omit<
  LookupConfigModalPropsWithoutExtraState,
  'onRequestSubmit' | 'initialLookupConfig' | 'onRequestClose'
>;

export function indexSchemasById(schemas: Array<Schema>): {[schemaId: string]: Schema} {
  return objectFromEntries(schemas.map((schema) => [schema.id, schema]));
}

/**
 * For SCHEMA_FIELD, COUNT, RESULTS, and CONTAINER_BY_GRID_POSITION lookup steps in a snapshot fields context, we want to
 * exclude certain field options:
 *
 * 1) Snapshot fields --> This is to avoid creating a dependency tree of snapshot fields before computing
 * their values. This doesn't remove any meaningful functionality because any lookup based on a snapshot field
 * could just implement the steps of that snapshot field itself.
 * 2) Computed fields --> Same reasons as 1), though *currently* not all computed fields can be re-implemented
 * as a snapshot field
 * 3) (For first step, SCHEMA_FIELD & COUNT only) Non-link fields (including blob links and dropdown) -->
 * Since nothing can be looked up from non-link fields except their direct value, a snapshot field starting at
 * a non-link field could only ever be a copy of another field
 */
export function shouldShowSchemaFieldForSnapshotFieldContext(
  field: SchemaField,
  excludeNonLinkFields: boolean
): boolean {
  return (
    !field.isSnapshot &&
    !field.isComputed &&
    (excludeNonLinkFields
      ? LINK_FIELD_TYPES.includes(field.fieldType) &&
        ![FIELD_TYPES.BLOB_LINK.type, FIELD_TYPES.SELECTOR.type].includes(field.fieldType)
      : true)
  );
}

export function shouldShowSchemaFieldInTreeForSnapshotFieldContext(field: SchemaField): boolean {
  return shouldShowSchemaFieldForSnapshotFieldContext(field, true);
}

export function getSnapshotFieldLookupConfigModalProps(
  baseSchema: Schema | null,
  additionalIntermediateLookupStepTypes: Array<LookupStepType> = []
): SnapshotFieldLookupConfigModalProps | undefined {
  const intermediateLookupStepTypes = [
    LookupStepType.BOX,
    LookupStepType.PLATE,
    LookupStepType.CONTENTS,
    LookupStepType.CONTAINER,
    LookupStepType.RESULTS,
    LookupStepType.WELLS,
    LookupStepType.BOX_CONTAINERS,
    LookupStepType.FILTER,
    LookupStepType.LOCATION,
    ...additionalIntermediateLookupStepTypes,
  ];
  return baseSchema
    ? {
        schemaFieldFilterFn: shouldShowSchemaFieldForSnapshotFieldContext,
        schemaFieldTreeFilterFn: shouldShowSchemaFieldInTreeForSnapshotFieldContext,
        defaultIsMultiValue: false,
        baseSchema,
        firstLookupStepTypes: FIRST_LOOKUP_STEP_TYPES,
        intermediateLookupStepTypes,
        finalLookupStepTypes: [
          LookupStepType.ALIASES,
          LookupStepType.AVAILABLE_SPACE,
          LookupStepType.NAME,
          LookupStepType.BOX_CONTAINERS,
          LookupStepType.SCHEMA_FIELD,
          LookupStepType.WELLS,
          LookupStepType.COUNT,
          LookupStepType.WELL_COORDINATES,
          LookupStepType.QUANTITY,
          LookupStepType.CONCENTRATION,
          LookupStepType.REGISTRY_IDENTIFIER,
          LookupStepType.RESIDUES,
          LookupStepType.CHEMICAL_STRUCTURE,
          LookupStepType.FILTER,
          LookupStepType.CONTENTS,
          LookupStepType.BOX,
          LookupStepType.PLATE,
          LookupStepType.CONTAINER_BY_GRID_POSITION,
          LookupStepType.CONTAINER,
          LookupStepType.LOCATION,
          LookupStepType.BARCODE,
          LookupStepType.LOCATION_PATH,
          LookupStepType.COMPUTED_EXPIRATION_DATE,
          LookupStepType.FREEZE_THAW_COUNT,
          LookupStepType.STUDIES,
        ],
      }
    : undefined;
}
